import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import React, { useState, useEffect, useMemo } from "react";
import MDButton from "../../components/MDButton";
import MDInput from "../../components/MDInput";
import ProductEdit from "./edits/ProductEdit";
import buildComboTable from "layouts/product/data/comboTableData";
import buildProductTable from "layouts/product/data/projectsTableData";
import ComboEdit from "./edits/ComboEdit";
import apis from "../../context/api";
import { timeOf } from "../../utils/format";
import NoAccess from "components/NoAccess";
import { canView, canCreate, canUpdate } from "context/permissions";

function Products() {
  const allowComboView = canView("Combo");
  const allowComboCreate = canCreate("Combo");
  const allowComboUpdate = canUpdate("Combo");
  const allowProductView = canView("Product");
  const allowProductCreate = canCreate("Product");
  const allowProductUpdate = canUpdate("Product");
  const [comboUpdate, setComboUpdate] = useState(false);
  const [productUpdate, setProductUpdate] = useState(false);
  const [id, setId] = useState(null);
  const [comboList, setComboList] = useState([]);
  const [productList, setProductList] = useState([]);
  const [comboSearch, setComboSearch] = useState("");
  const [productSearch, setProductSearch] = useState("");
  const handleCancel = () => {
    setProductUpdate(false);
    setComboUpdate(false);
  };
  const productUpdated = () => {
    setProductUpdate(false);
    setComboUpdate(false);
    loadTableData();
  };
  const comboUpdated = () => {
    setProductUpdate(false);
    setComboUpdate(false);
    loadTableData();
  };
  useEffect(() => {
    loadTableData();
  }, []);
  const editProduct = (id) => {
    setId(id);
    setProductUpdate(true);
  };
  const editCombo = (id) => {
    setId(id);
    setComboUpdate(true);
  };

  const loadTableData = async () => {
    try {
      apis
        .getCombos()
        .then((comboData) => {
          setComboList(Array.isArray(comboData) ? comboData : []);
        })
        .catch((err) => {
          throw new Error("Can not get Combos");
        });

      apis
        .getProducts()
        .then((productData) => {
          setProductList(Array.isArray(productData) ? productData : []);
        })
        .catch((err) => {
          throw new Error("Can not get Products");
        });
    } catch (err) {
      console.error("Data loading error:", err);
    }
  };

  // Newest first; records without updatedAt sort to the bottom.
  const sortByUpdatedDesc = (a, b) => timeOf(b.updatedAt) - timeOf(a.updatedAt);

  const matches = (term, ...fields) => {
    const q = term.trim().toLowerCase();
    if (!q) return true;
    return fields.some((f) => (f || "").toLowerCase().includes(q));
  };

  const comboData = useMemo(() => {
    const filtered = comboList
      .filter((item) => matches(comboSearch, item.name, item.shortName))
      .sort(sortByUpdatedDesc);
    return buildComboTable(filtered, (cid) => editCombo(cid), allowComboUpdate);
  }, [comboList, comboSearch, allowComboUpdate]);

  const projectData = useMemo(() => {
    const filtered = productList
      .filter((item) => matches(productSearch, item.productName, item.productShortName))
      .sort(sortByUpdatedDesc);
    return buildProductTable(filtered, (pid) => editProduct(pid), allowProductUpdate);
  }, [productList, productSearch, allowProductUpdate]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {productUpdate && <ProductEdit onSuccess={productUpdated} id={id} onCancel={handleCancel} />}
      {comboUpdate && <ComboEdit onSuccess={comboUpdated} id={id} onCancel={handleCancel} />}
      {!productUpdate && !comboUpdate && !allowComboView && !allowProductView && (
        <MDBox pt={6} pb={6}>
          <Grid container spacing={6}>
            <Grid item xs={12}>
              <NoAccess message="You do not have permission to view Products or Combos." />
            </Grid>
          </Grid>
        </MDBox>
      )}
      {!productUpdate && !comboUpdate && (allowComboView || allowProductView) && (
        <MDBox pt={6} pb={6}>
          <Grid container spacing={6}>
            {allowComboView && (
              <Grid item xs={12}>
                <Card>
                  <MDBox
                    mx={2}
                    mt={-3}
                    py={3}
                    px={2}
                    variant="gradient"
                    bgColor="info"
                    borderRadius="lg"
                    coloredShadow="info"
                  >
                    <MDTypography variant="h6" color="white">
                      Combo Table
                    </MDTypography>
                  </MDBox>
                  <Grid
                    container
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={2}
                    px={2}
                    pt={2}
                  >
                    <Grid item xs={12} sm={6} md={4}>
                      <MDInput
                        fullWidth
                        label="Search by name or code"
                        value={comboSearch}
                        onChange={(e) => setComboSearch(e.target.value)}
                      />
                    </Grid>
                    {allowComboCreate && (
                      <Grid item>
                        <MDButton
                          variant="gradient"
                          color="info"
                          onClick={() => {
                            setId(null);
                            setComboUpdate(true);
                          }}
                        >
                          Create
                        </MDButton>
                      </Grid>
                    )}
                  </Grid>
                  <MDBox pt={3}>
                    <DataTable
                      table={{ columns: comboData.columns, rows: comboData.rows }}
                      isSorted={false}
                      entriesPerPage={false}
                      showTotalEntries={false}
                      noEndBorder
                    />
                  </MDBox>
                </Card>
              </Grid>
            )}
            {allowProductView && (
              <Grid item xs={12}>
                <Card>
                  <MDBox
                    mx={2}
                    mt={-3}
                    py={3}
                    px={2}
                    variant="gradient"
                    bgColor="info"
                    borderRadius="lg"
                    coloredShadow="info"
                  >
                    <MDTypography variant="h6" color="white">
                      Product Table
                    </MDTypography>
                  </MDBox>
                  <Grid
                    container
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={2}
                    px={2}
                    pt={2}
                  >
                    <Grid item xs={12} sm={6} md={4}>
                      <MDInput
                        fullWidth
                        label="Search by name or code"
                        value={productSearch}
                        onChange={(e) => setProductSearch(e.target.value)}
                      />
                    </Grid>
                    {allowProductCreate && (
                      <Grid item>
                        <MDButton
                          variant="gradient"
                          color="info"
                          onClick={() => {
                            setId(null);
                            setProductUpdate(true);
                          }}
                        >
                          Create
                        </MDButton>
                      </Grid>
                    )}
                  </Grid>
                  <MDBox pt={3}>
                    <DataTable
                      table={{ columns: projectData.columns, rows: projectData.rows }}
                      isSorted={false}
                      entriesPerPage={false}
                      showTotalEntries={false}
                      noEndBorder
                    />
                  </MDBox>
                </Card>
              </Grid>
            )}
          </Grid>
        </MDBox>
      )}
      <Footer />
    </DashboardLayout>
  );
}

export default Products;
